import {
    ActiveWireFlowAnchor,
    FlowAnchor,
    FlowBlock,
    FlowPoint,
    FlowPort,
    FlowPortContext,
    FlowPortKind,
    FlowWire
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {FlowUtil} from '@mintdata-wsp/common/util/FlowUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {FlowPortPopup} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/canvas/FlowPortPopup';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {IPopupManager, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./FlowPortView.less');
const cx = classnames.bind(styles);

export type PortRef = HTMLElement | SVGElement;

export interface FlowPortViewProps
{
    isHighlighted:boolean;
    isSelected:boolean;
    isValid:boolean;
    offset:FlowPoint;
    flowBlock:FlowBlock;
    flowPort:FlowPort;
    onMouseDown:React.EventHandler<React.MouseEvent<any>>;
    onMouseUpOrOut:React.EventHandler<React.MouseEvent<any>>;
}

@observer
export class FlowPortView extends BaseComponentWithContext<FlowPortViewProps, any>
{
    public static defaultProps:Partial<FlowPortViewProps> = {
        isValid: true
    };

    private _popupId:GUID = IdUtil.newGUID();

    @observable
    private _isPopupVisible = false;

    _renderOutputPort()
    {
        const {onMouseDown, onMouseUpOrOut, isHighlighted, isSelected} = this.props;
        const absolutePosition = this._getAbsolutePositionStyle();
        const popupManager:IPopupManager = this.context.popupManager;

        return (
            <div
                className={cx('outputPortHolder')}
                ref={popupManager.position(this._popupId, SourceAnchor.CenterTop, PopupAnchor.CenterBottom)}
                style={absolutePosition}
            >
                <svg
                    onMouseDown={onMouseDown}
                    onMouseUp={onMouseUpOrOut}
                    onMouseOut={onMouseUpOrOut}
                    onMouseEnter={this._onPortMouseEnter}
                    onMouseLeave={this._onPortMouseLeave}

                    className={cx('outputPortCircle',
                        {'isSelected': !isHighlighted && isSelected},
                        {'isHighlighted': isHighlighted}
                    )} viewBox={'0 0 100 200'}
                >
                    <path
                        d="M 6.123233995736766e-15 200 A 100 100 0 0 0 6.123233995736766e-15 0 L 0 100 L 6.123233995736766e-15 200" />
                </svg>

                {this._renderTooltipPopup()}

            </div>
        );
    }

    private _renderInputPort()
    {
        const {onMouseDown, onMouseUpOrOut, isHighlighted, isSelected, isValid} = this.props;
        const absolutePosition = this._getAbsolutePositionStyle();
        const popupManager:IPopupManager = this.context.popupManager;

        return (
            <div className={cx('inputMouseTargetArea')}
                 style={absolutePosition}
                 onMouseDown={onMouseDown}
                 onMouseUp={onMouseUpOrOut}
                 onMouseOut={onMouseUpOrOut}
                 onMouseEnter={this._onPortMouseEnter}
                 onMouseLeave={this._onPortMouseLeave}
                 ref={popupManager.position(this._popupId, SourceAnchor.CenterTop, PopupAnchor.CenterBottom)}
            >
                <div className={cx('inputPortTriangle', {
                        isSelected: !isHighlighted && isSelected,
                        isHighlighted: isHighlighted,
                        isInvalid: !isValid
                    }
                )} />

                {this._renderTooltipPopup()}
            </div>
        );
    }

    private _getAbsolutePositionStyle()
    {
        const {offset, flowPort} = this.props;
        const isInputPort = flowPort.kind === FlowPortKind.InputPort;

        const style:any = {};
        style.top = offset.y;

        if( isInputPort )
            style.left = offset.x;
        else
            style.right = offset.x;

        return style;
    }

    @action.bound
    private _onPortMouseEnter(e:any):void
    {
        const {flowBlock, flowPort} = this.props;
        this._isPopupVisible = true;

        const portPosition = FlowUtil.getAbsolutePortPosition(flowBlock, flowPort);

        const ctx = new FlowPortContext(flowBlock, flowPort, portPosition);
        controllers.ui.flowCanvas.setMouseOverPort(ctx);
    }

    @action.bound
    private _onPortMouseLeave(e:any):void
    {
        const {flowBlock, flowPort} = this.props;
        this._isPopupVisible = false;

        const ctx = controllers.ui.flowCanvas.getMouseOverPort();
        const isThisPort = ctx && flowBlock.id === ctx.flowBlock.id && flowPort.id === ctx.flowPort.id;

        if( isThisPort )
            controllers.ui.flowCanvas.clearMouseOverPort();
    }

    @bind
    private _onPortMouseDown(e:any):void
    {
        const {onMouseDown} = this.props;
        onMouseDown(e);
    }

    @action.bound
    private _onDragStart(e:MouseEvent, data:DraggableData):any
    {
        this._updateActiveWire(e, data);
    }

    @action.bound
    private _onDragInProgress(e:MouseEvent, data:DraggableData):void
    {
        this._updateActiveWire(e, data);
    }

    private _updateActiveWire(e:MouseEvent, data:DraggableData):void
    {
        const {flowBlock, flowPort} = this.props;

        const fromPosition = FlowUtil.getAbsolutePortPosition(flowBlock, flowPort);
        if( !fromPosition )
            return;

        const canvasTopLeft = controllers.ui.flowCanvas.getCanvasTopLeft();
        if( !canvasTopLeft )
            return;

        const mousePosition = new FlowPoint(e.clientX - canvasTopLeft.x, e.clientY - canvasTopLeft.y);

        const sourceAnchor = new FlowAnchor(flowBlock, flowPort);
        const targetAnchor = new ActiveWireFlowAnchor(mousePosition);

        let flowWire;
        const {kind} = flowPort;
        if( kind === FlowPortKind.OutputPort )
            flowWire = new FlowWire(sourceAnchor, targetAnchor);
        else
            flowWire = new FlowWire(targetAnchor, sourceAnchor);

        controllers.ui.flowCanvas.setActiveWire(flowWire);
    }

    @action.bound
    private _onDragStop(e:MouseEvent, data:DraggableData):void
    {
        const controller = controllers.ui.flowCanvas;
        const {flowBlock, flowPort} = this.props;
        const thisPortAnchor = new FlowAnchor(flowBlock, flowPort);

        const portCtx = controller.getMouseOverPort();
        if( portCtx &&
            portCtx.flowPort.id !== flowPort.id &&
            portCtx.flowBlock.id !== flowBlock.id )
        {
            const targetAnchor = new FlowAnchor(portCtx.flowBlock, portCtx.flowPort);

            let flowWire;
            const {kind} = flowPort;
            if( kind === FlowPortKind.OutputPort )
                flowWire = new FlowWire(thisPortAnchor, targetAnchor);
            else
                flowWire = new FlowWire(targetAnchor, thisPortAnchor);

            controller.addPermanentWire(flowWire);
        }
        else
        {
            const blockCtx = controller.getMouseOverBlock();
            if( blockCtx && blockCtx.flowBlock.id !== flowBlock.id )
            {
                // console.log(`PORT, drag stop, ${this.props.flowBlock.component.name} blockCtx: ${blockCtx}`, blockCtx);

                const isOutputPort = flowPort.kind === FlowPortKind.OutputPort;
                let targetAnchor:FlowAnchor;

                if( isOutputPort )
                    targetAnchor = blockCtx.defaultInputAnchor;
                else
                    targetAnchor = blockCtx.defaultOutputAnchor;

                // Target anchor exists if only exactly 1 port of this kind exists on the block,
                // making it un-ambiguous which port the user intended to connect to.
                if( targetAnchor )
                {
                    if( isOutputPort )
                        controller.addPermanentWire(new FlowWire(thisPortAnchor, targetAnchor));
                    else
                        controller.addPermanentWire(new FlowWire(targetAnchor, thisPortAnchor));
                }
            }
        }

        controller.clearMouseOverPort();
        controller.clearActiveWire();
    }

    private _renderPort()
    {
        const {flowPort} = this.props;
        const {kind} = flowPort;

        if( kind == FlowPortKind.InputPort )
            return this._renderInputPort();
        else if( kind == FlowPortKind.OutputPort )
            return this._renderOutputPort();
    }

    private _renderTooltipPopup()
    {
        return (
            <Popover id={this._popupId}>
                <FlowPortPopup port={this.props.flowPort} isVisible={this._isPopupVisible} />
            </Popover>
        );
    }

    render()
    {
        return (
            <DraggableCore onStart={this._onDragStart}
                           onDrag={this._onDragInProgress}
                           onStop={this._onDragStop}
                           onMouseDown={this._onPortMouseDown}
                           enableUserSelectHack={false}
            >
                {this._renderPort()}
            </DraggableCore>
        );
    }
}




